import { useEffect } from 'react'
import { Eye, Clock, Users, TrendingUp, Globe, Monitor } from 'lucide-react'
import { useAnalyticsStore } from '../stores/analyticsStore'
import {
  ViewsLineChart,
  StatsBarChart,
  StatsPieChart,
  ChartLegend,
} from '../components/AnalyticsChart'
import { StatCard, Spinner, EmptyState } from '../components/common'
import { formatDuration, formatNumber } from '../hooks'

export default function Analytics() {
  const { dashboard, isLoading, error, fetchDashboard, periodDays, setPeriodDays } =
    useAnalyticsStore()

  useEffect(() => {
    fetchDashboard()
  }, [fetchDashboard])

  if (isLoading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <EmptyState
        title="Failed to load analytics"
        description={error}
        action={
          <button onClick={() => fetchDashboard()} className="btn-primary">
            Try Again
          </button>
        }
      />
    )
  }

  if (!dashboard) {
    return (
      <EmptyState
        icon={<TrendingUp className="w-12 h-12" />}
        title="No analytics data"
        description="Start sharing videos to see analytics"
      />
    )
  }

  const { summary, views_by_day, geo_breakdown, device_breakdown } = dashboard

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Analytics</h1>
          <p className="text-slate-400">Detailed insights into your video performance</p>
        </div>

        <select
          value={periodDays}
          onChange={(e) => {
            setPeriodDays(Number(e.target.value))
            fetchDashboard(Number(e.target.value))
          }}
          className="select w-48"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Views"
          value={formatNumber(summary.total_views)}
          icon={<Eye className="w-5 h-5" />}
        />
        <StatCard
          label="Unique Viewers"
          value={formatNumber(summary.unique_viewers)}
          icon={<Users className="w-5 h-5" />}
        />
        <StatCard
          label="Total Watch Time"
          value={formatDuration(summary.total_watch_time)}
          icon={<Clock className="w-5 h-5" />}
        />
        <StatCard
          label="Avg. Completion"
          value={`${Math.round(summary.avg_completion)}%`}
          icon={<TrendingUp className="w-5 h-5" />}
        />
      </div>

      {/* Views over time */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Views Over Time</h3>
        {views_by_day.length > 0 ? (
          <ViewsLineChart
            data={views_by_day}
            dataKey="views"
            xDataKey="date"
            height={350}
          />
        ) : (
          <div className="h-64 flex items-center justify-center text-slate-400">
            No view data for this period
          </div>
        )}
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Geographic breakdown */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-slate-400" />
            <h3 className="text-lg font-semibold text-white">Geographic Distribution</h3>
          </div>

          {geo_breakdown.length > 0 ? (
            <StatsBarChart
              data={geo_breakdown.map((g) => ({
                name: g.country_name || g.country,
                views: g.views,
              }))}
              dataKey="views"
              nameKey="name"
              layout="vertical"
              height={300}
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-400">
              No location data available
            </div>
          )}
        </div>

        {/* Device breakdown */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Monitor className="w-5 h-5 text-slate-400" />
            <h3 className="text-lg font-semibold text-white">Device Breakdown</h3>
          </div>

          {device_breakdown.length > 0 ? (
            <>
              <StatsPieChart
                data={device_breakdown.map((d) => ({
                  name: d.device_type,
                  value: d.views,
                }))}
                dataKey="value"
                height={250}
              />
              <ChartLegend
                items={device_breakdown.map((d) => ({
                  name: d.device_type,
                  value: `${d.views} views`,
                }))}
              />
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-400">
              No device data available
            </div>
          )}
        </div>
      </div>

      {/* Additional insights */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Engagement Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Best Performing Day</p>
            <p className="text-xl font-semibold text-white mt-1">
              {views_by_day.length > 0
                ? views_by_day.reduce((best, day) =>
                    day.views > best.views ? day : best
                  ).date
                : 'N/A'}
            </p>
          </div>
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Avg. Views Per Day</p>
            <p className="text-xl font-semibold text-white mt-1">
              {views_by_day.length > 0
                ? Math.round(
                    views_by_day.reduce((sum, day) => sum + day.views, 0) /
                      views_by_day.length
                  )
                : 0}
            </p>
          </div>
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Active Tracking Links</p>
            <p className="text-xl font-semibold text-white mt-1">
              {summary.total_links}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
